import {HttpClient, HttpContext, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ApiConstants} from '@coreConstant';
import {PaginationModel, QuoteOrdersModel, RequestParamModel, ResponseModel} from '@coreModels';
import {catchError, map, Observable, throwError} from 'rxjs';
import {BaseService} from './base.service';
import {SKIP_ERROR_TOAST} from '../../interceptors/http-context.tokens';

/**
 * Loose request-body shape for quote/order write endpoints. These payloads are assembled
 * dynamically (form values + computed fields) and do NOT map 1:1 to QuoteOrdersModel, so an
 * index signature is used, plus the few identifier fields this service reads for URL building
 * and `delete`.
 */
type QuoteRequestBody = {
  id?: string | number;
  quote_id?: string | number;
  export_id?: string | number;
  [key: string]: unknown;
};

@Injectable({
  providedIn: 'root'
})
export class QuoteOrdersService {

  constructor(private http: HttpClient, private baseService: BaseService) {
  }

  createQuoteOrder(data: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_CREATE_URL, data);
  }

  saveQuoteProduct(quoteProductModel: QuoteRequestBody) {
    if (quoteProductModel.id) {
      let id = quoteProductModel.id;
      delete quoteProductModel.id;
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_UPDATE_URL + id, quoteProductModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_CREATE_URL, quoteProductModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  saveQuoteProductForCreateQuote(quoteProductModel: QuoteRequestBody) {
    if (quoteProductModel.id) {
      let id = quoteProductModel.id;
      delete quoteProductModel.id;
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_UPDATE_FOR_QUOTE_CREATE_URL + id, quoteProductModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_CREATE_FOR_QUOTE_CREATE_URL, quoteProductModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  saveQuoteProductByExportID(quoteProductModel: QuoteRequestBody, exportID: string) {
    if (quoteProductModel.id) {
      // delete quoteProductModel.id;
      return this.http.patch<ResponseModel>(ApiConstants.LIVE_LINK_PRODUCT_UPDATE_URL + exportID, quoteProductModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.LIVE_LINK_PRODUCT_CREATE_URL + exportID, quoteProductModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  saveQuoteSummary(quoteSummary: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_SUMMARY_CREATE_URL, quoteSummary)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateQuoteSummary(quoteSummary: QuoteRequestBody, quoteSummaryID: number = 0) {
    if (quoteSummaryID > 0) {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_SUMMARY_CREATE_URL + '/' + quoteSummaryID, quoteSummary)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_SUMMARY_CREATE_URL, quoteSummary)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  /**
   * Save or update quote product calculator
   * @param quoteProductAppliedCalculatorModel Calculator data to save
   * @returns Observable with full response data including affected_calculators and quote_summary
   */
  saveQuoteProductAppliedCalculator(quoteProductAppliedCalculatorModel: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_APPLIED_CALCULATOR_CREATE_URL, quoteProductAppliedCalculatorModel)
      .pipe(map((response) => {
        // Return full data object with affected_calculators and quote_summary
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  saveQuoteProductAppliedCalculatorForQuoteCreate(quoteProductAppliedCalculatorModel: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_APPLIED_CALCULATOR_CREATE_URL, quoteProductAppliedCalculatorModel)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  saveQuoteProductAppliedCalculatorByExportId(quoteProductAppliedCalculatorModel: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_APPLIED_CALCULATOR_CREATE_BY_EXPORT_ID_URL, quoteProductAppliedCalculatorModel)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  quoteScheduleDelivery(requestData: QuoteRequestBody, id?: number | string) {
    if (id) {
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_SCHEDULE_DELIVERY_URL + '/' + id, requestData)
        .pipe(map((response) => response.data), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_SCHEDULE_DELIVERY_URL, requestData)
        .pipe(map((response) => response.data), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  quoteScheduleDeliveryByExportID(quoteScheduleDeliveryModel: QuoteRequestBody, exportID: string) {
    if (quoteScheduleDeliveryModel.id) {
      // delete quoteProductModel.id;
      return this.http.patch<ResponseModel>(ApiConstants.LIVE_LINK_QUOTE_SCHEDULE_DELIVERY_UPDATE_URL + exportID, quoteScheduleDeliveryModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.LIVE_LINK_QUOTE_SCHEDULE_DELIVERY_CREATE_URL + exportID, quoteScheduleDeliveryModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  quoteScheduleDeliveryProduct(requestData: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_SCHEDULE_DELIVERY_PRODUCT_URL, requestData)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  quoteScheduleDeliveryProductBulk(requestData: QuoteRequestBody[]) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_SCHEDULE_DELIVERY_PRODUCT_BULK_URL, requestData)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateQuoteProductSplitDelivery(id: number | string, requestData: QuoteRequestBody) {
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_PRODUCT_SCHEDULE_DATE_UPDATE + '/' + id, requestData)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  reCalculateQuotePrice(quoteModel: QuoteRequestBody, id?: number | string) {
    if (id) {
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_RE_CALCULATE_PRICE_URL + '/' + id, quoteModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_RE_CALCULATE_PRICE_URL, quoteModel)
        .pipe(map((response) => response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  reCalculateQuotePriceByExportId(quoteModel: QuoteRequestBody) {
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_RE_CALCULATE_PRICE_BY_EXPORT_ID_URL + quoteModel.id, quoteModel)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getQuoteOrders(requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_LIST_URL + requestParamModel.getParams(), {observe: 'response'})
      .pipe(map((response: HttpResponse<ResponseModel>) => {
        const accountList = new PaginationModel<QuoteOrdersModel>();
        const headers = response.headers;
        accountList.setHeaders(headers);
        accountList.data = response.body!.data as QuoteOrdersModel[];
        return accountList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getQuoteData(quoteID: number, requestParamModel?: RequestParamModel): Observable<QuoteOrdersModel> {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_DETAIL_URL + quoteID + requestParamModel?.getParams())
      .pipe(map((response) => response.data as QuoteOrdersModel), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  deleteQuoteProducts(id: number | string) {
    return this.http.delete(ApiConstants.QUOTE_ORDER_PRODUCT_DELETE_URL + id);
  }

  deleteQuoteProductsByExportID(exportId: number | string, id: number | string) {
    return this.http.delete(ApiConstants.LIVE_LINK_PRODUCT_DELETE_URL + exportId + '/' + id);
  }

  /**
   * Delete quote product calculator
   * @param id Calculator ID to delete
   * @returns Observable with full response data including affected_calculators and quote_summary
   */
  deleteQuoteProductAppliedCalculator(id: number | string) {
    return this.http.delete<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_APPLIED_CALCULATOR_DELETE_URL + id)
      .pipe(map((response) => {
        // Match save method pattern - return data object
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  deleteQuoteProductAppliedCalculatorByExportId(exportId: number | string, id: number | string) {
    return this.http.delete<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_APPLIED_CALCULATOR_DELETE_BY_EXPORT_ID_URL + '/' + exportId + '/' + id)
      .pipe(map((response) => response), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getConsumableDetail(requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_PRODUCT_APPLIED_CALCULATOR_LIST_URL + requestParamModel.getParams(), {observe: 'response'})
      .pipe(map((response: HttpResponse<ResponseModel>) => {
        const regionList = new PaginationModel<QuoteOrdersModel>();
        const headers = response.headers;
        regionList.setHeaders(headers);
        regionList.data = response.body!.data;
        return regionList;
        // return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  reactiveQuote(data: QuoteRequestBody, isExportId?: boolean) {
    let quoteID = data.quote_id;
    const apiUrl = isExportId ? ApiConstants.QUOTE_ORDER_REACTIVATE_BY_EXPORT_URL : ApiConstants.QUOTE_ORDER_REACTIVATE_URL;
    delete data.quote_id;
    return this.http.patch<ResponseModel>(apiUrl + '/' + quoteID, data)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  deleteQuoteOrders(_id: number | string) {
    return this.http.delete<ResponseModel>(ApiConstants.QUOTE_ORDER_DELETE_URL + _id)
      .pipe(map(() => ''), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getCounter(requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_COUNTER_URL + requestParamModel?.getParams())
      .pipe(map((response) => response.data as QuoteOrdersModel), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  quoteExportPDF(endpoint: string, formData: FormData | QuoteRequestBody) {
    return this.http.post<ResponseModel>(endpoint, formData)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getTotalSampleProductCounter(requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_SAMPLE_PRODUCT_COUNTER_URL + requestParamModel?.getParams())
      .pipe(map((response) => response.data as QuoteOrdersModel),
        catchError((err: unknown) => {
          return throwError(() => err);
        }));
  }

  getSampleDetails(quoteID: number, requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_DETAIL_URL + quoteID + requestParamModel.getParams(), {observe: 'response'})
      .pipe(map((response: HttpResponse<ResponseModel>) => {
        const sampleList = new PaginationModel<QuoteOrdersModel>();
        const headers = response.headers;
        sampleList.setHeaders(headers);
        sampleList.data = response.body!.data as QuoteOrdersModel[];
        return sampleList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getOrderCounts(requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDERS_COUNTS_URL + requestParamModel.getParams())
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getTruckDetail(requestParamModel: RequestParamModel) {
    return this.baseService.get(ApiConstants.QUOTE_ORDER_TRUCK_TYPE_LIST_URL, requestParamModel)
      .pipe(map((response) => {
          return (response.body as ResponseModel).data;
        }),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  addReviewToQuote(quoteReviewModel: QuoteRequestBody) {
    return this.baseService.post(ApiConstants.QUOTE_ORDER_FEEDBACK_URL, quoteReviewModel).pipe(map((response) => {
        return (response as ResponseModel).data;
      }),
      catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getShippingCost(requestData: QuoteRequestBody) {
    return this.baseService.post(ApiConstants.QUOTE_ORDER_SHIPPING_CALCULATION_URL, requestData)
      .pipe(map((response) => {
        let quoteModel = (response as ResponseModel).data;
        return quoteModel;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  saveQuoteOrder(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    if (requestData.id) {
      let requestDataID = requestData.id;
      delete requestData.id;
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_UPDATE_URL + requestDataID, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_CREATE_URL, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  saveQuoteOrderAddress(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    if (requestData.id) {
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_ADDRESS_UPDATE_URL + '/' + requestData.id, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_ADDRESS_CREATE_URL, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  quoteOrderAddressByExportId(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    return this.http.patch<ResponseModel>(ApiConstants.UPDATE_ADDRESS_BY_EXPORT_ID + requestData.export_id, requestData)
      .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  saveQuoteOrderSiteContact(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    if (requestData.id) {
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_SITE_CONTACT_URL + '/' + requestData.id, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_SITE_CONTACT_URL, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  saveQuoteOrderByExportId(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_SITE_CONTACT_UPDATE_BY_EXPORT_ID_URL, requestData)
      .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  changeStatusByExportId(requestData: QuoteRequestBody): Observable<number> {
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_STATUS_UPDATE_BY_EXPORT_ID_URL + requestData.export_id, requestData)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  changeStatus(requestData: QuoteRequestBody) {
    let requestDataID = requestData.id;
    delete requestData.id;
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_STATUS_UPDATE_URL + requestDataID, requestData)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  changeOrderStatus(requestData: QuoteRequestBody) {
    let requestDataID = requestData.id;
    delete requestData.id;
    return this.http.patch<ResponseModel>(ApiConstants.ORDER_STATUS_UPDATE_URL + requestDataID, requestData)
      .pipe(map((response) => response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateSiteContactDetail(endpoint: string, accountModel: QuoteRequestBody) {
    return this.http.patch<ResponseModel>(endpoint, accountModel).pipe(
      map((response) => {
        return response?.data;
      }),
      catchError((err: unknown) => {
        return throwError(() => err);
      })
    );
  }

  updateDeliveryAddressByExportId(endpoint: string, accountModel: QuoteRequestBody) {
    return this.http.patch<ResponseModel>(endpoint, accountModel).pipe(
      map((response) => {
        return response?.data;
      }),
      catchError((err: unknown) => {
        return throwError(() => err);
      })
    );
  }

  deleteAllQuoteProductAppliedCalculators(_id: number | string) {
    return this.http.delete(ApiConstants.QUOTE_ORDER_DELETE_ALL_PRODUCT_APPLIED_CALCULATOR_URL + _id);
  }

  deleteAllQuoteProductAppliedCalculatorsByExportId(exportId: number | string, id: number | string) {
    return this.http.delete(ApiConstants.QUOTE_ORDER_DELETE_ALL_PRODUCT_APPLIED_CALCULATOR_URL_BY_EXPORT_ID + exportId + '/' + id);
  }

  getQuotePayment(formData: FormData | QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PAYMENT_LIST_URL, formData)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /* ── Pay Advantage payment requests (card / PayID / PayTo on their page) ─────── */

  /** Whether payment requests are switched on server-side. Public — no auth needed. */
  getPaymentRequestConfig() {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_PAYMENT_REQUEST_CONFIG_URL)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /** Create (or re-use) the payment request for a logged-in customer's payment; returns {url}. */
  createPaymentRequest(formData: FormData | QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PAYMENT_REQUEST_CREATE_URL, formData)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /** Create (or re-use) the payment request on the guest / share-link checkout; returns {url}. */
  createPaymentRequestByExportId(body: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PAYMENT_REQUEST_CREATE_BY_EXPORT_ID_URL, body)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  uploadPaymentReceiptByExportId(requestModel: FormData | QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_UPLOAD_PAYMENT_RECEIPT_URL, requestModel).pipe(
      map((response) => response.body), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  downloadQuoteOrder(param: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_DOWNLOAD_URL, param)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  downloadQuoteOrderByExportId(param: QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_DOWNLOAD_BY_EXPORT_ID_URL, param)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  downloadQuoteOrderNew(body: { file_name: string }) {
    return this.baseService.getDownloadFileContent(ApiConstants.QUOTE_ORDER_DOWNLOAD_FILE_URL + `${body.file_name}`);
  }

  downloadQuoteOrderFileByExportId(body: { file_name: string }, exportId: string) {
    return this.baseService.getDownloadFileContent(ApiConstants.QUOTE_ORDER_DOWNLOAD_FILE_BY_EXPORT_ID_URL + `${body.file_name}` + '&id=' + `${exportId}`);
  }

  getQuoteDetailByExportId(requestParamModel: RequestParamModel, exportId: string): Observable<QuoteOrdersModel> {
    // Build URL with enhanced cache-busting to avoid stale cached responses in dev/prod builds
    // Add random component to ensure fresh data, especially important for live link users
    const base = `${ApiConstants.QUOTE_ORDER_DETAIL_BY_EXPORT_ID}/${exportId}`;
    const params = requestParamModel.getParams();
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    const cacheBuster = `_=${timestamp}&r=${random}`;
    const url = `${base}${params ? params + '&' + cacheBuster : '?' + cacheBuster}`;

    return this.http.get<ResponseModel>(url).pipe(
      map((response) => response.data as QuoteOrdersModel),
      catchError((err: unknown) => throwError(() => err))
    );
  }

  getQuoteStatusByExportId(type: string, exportID: number, requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.GET_STATUS_BY_EXPORT_ID + type + '/' + exportID + requestParamModel.getParams())
      .pipe(map((response) => response.data as QuoteOrdersModel), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getQuoteStatusByTrackId(trackID: number, requestParamModel: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.GET_STATUS_BY_TRACK_ID + trackID + requestParamModel.getParams())
      .pipe(map((response) => response.data as QuoteOrdersModel), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getOrderDetailByTrackingId(requestParamModel: RequestParamModel, trackID: string): Observable<QuoteOrdersModel> {
    return this.http.get<ResponseModel>(`${ApiConstants.TRACK_ORDER_DETAIL_BY_ID_URL}/${trackID}${requestParamModel.getParams()}`)
      .pipe(map((response) => response.data as QuoteOrdersModel), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getPaymentLinkByExportId(formData: FormData | QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PAYMENT_LINK_BY_EXPORT_ID_URL, formData)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
   * Checks the payment the customer just made on Pay Advantage's page. Its only caller, the payment
   * response page, shows its own screen for every error, so the global error toast is skipped.
   */
  quoteValidateToken(formData: FormData | QuoteRequestBody) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_PAYMENT_VALIDATE_TOKEN_URL, formData,
      {context: new HttpContext().set(SKIP_ERROR_TOAST, true)})
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  confirmJob(exportId: string, status: string) {
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_CONFIRM_JOB_URL + exportId, {status})
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateDeliveryAddress(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    if (requestData.id) {
      return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_DELIVERY_DETAIL_UPDATE_URL + '/' + requestData.id, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    } else {
      return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_ADDRESS_URL, requestData)
        .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
          return throwError(() => err);
        }));
    }
  }

  updateDeliveryAddressByIdentifier(endpoint: string, accountModel: QuoteRequestBody) {
    return this.http.patch<ResponseModel>(endpoint, accountModel)
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateStockRequiredDate(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_STOCK_REQUIRED_DATE_UPDATE_URL + requestData.id, requestData)
      .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateStockRequiredDateByIdentifier(endpoint: string, accountModel: QuoteRequestBody) {
    return this.http.patch<ResponseModel>(endpoint, accountModel).pipe(
      map((response) => {
        return response?.data; // Return the `data` from the response
      }),
      catchError((err: unknown) => {
        return throwError(() => err); // Proper error handling
      })
    );
  }

  splitDeliveryUpdate(id: number) {
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_SPLIT_DELIVERY_UPDATE + id, null)
      .pipe(map((response) => response?.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateStatusOnAddressOrProductChange(requestData: QuoteRequestBody, returnResponse: boolean = false) {
    let id = requestData.id;
    delete requestData.id;
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_UPDATE_STATUS_ON_ADDRESS_OR_PRODUCT_CHANGE_URL + id, null)
      .pipe(map((response) => returnResponse ? response.data : response.data.id), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  updateStatusOnAddressOrProductChangeByIdentifier(endpoint: string, accountModel?: QuoteRequestBody) {
    return this.http.patch<ResponseModel>(endpoint, accountModel)
      .pipe(
        map((response) => response.data),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  updateQuoteOrder(orderModel: QuoteRequestBody): Observable<QuoteOrdersModel> {
    return this.http.patch<ResponseModel>(ApiConstants.QUOTE_ORDER_UPDATE_URL + orderModel.id, orderModel)
      .pipe(
        map((response) => response.data as QuoteOrdersModel),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  getQuoteStatus(type: string, quoteID: number, requestParamModel?: RequestParamModel) {
    return this.http.get<ResponseModel>(ApiConstants.QUOTE_ORDER_STATUS_URL + type + '/' + quoteID + (requestParamModel ? requestParamModel.getParams() : ''))
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }


  getScheduleDelivery(requestParamModel: RequestParamModel) {
    return this.baseService.get(ApiConstants.QUOTE_SCHEDULE_DELIVERY_URL, requestParamModel)
      .pipe(map((response) => {
        const scheduleDeliveryList = new PaginationModel<QuoteOrdersModel>();
        const headers = response.headers;
        scheduleDeliveryList.setHeaders(headers);
        scheduleDeliveryList.data = (response.body as ResponseModel).data;
        return scheduleDeliveryList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  getScheduleDeliveryByExportID(requestParamModel: RequestParamModel) {
    return this.baseService.get(ApiConstants.QUOTE_SCHEDULE_DELIVERY_EXPORT_URL, requestParamModel)
      .pipe(map((response) => {
        const scheduleDeliveryList = new PaginationModel<QuoteOrdersModel>();
        const headers = response.headers;
        scheduleDeliveryList.setHeaders(headers);
        scheduleDeliveryList.data = (response.body as ResponseModel).data;
        return scheduleDeliveryList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  downloadPDF(id: number, type: string) {
    return this.http.get(ApiConstants.QUOTE_ORDER_DOWNLOAD_URL + '/' + id + '/' + type, {responseType: 'blob'})
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- endpoint returns a Blob but existing code reads `.data` off it; preserved verbatim, tracked as a separate bug to fix.
      .pipe(map((response: any) => {
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
   * Validate quote SRD (Stock Required Date) - Authenticated endpoint
   * @param quoteId Quote ID
   * @returns Observable with validation result
   */
  validateQuoteSRD(quoteId: number) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_VALIDATE_SRD_URL + quoteId, {})
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
   * Validate quote SRD by export ID - Unauthenticated endpoint for live links
   * @param exportId Quote export ID
   * @returns Observable with validation result
   */
  validateQuoteSRDByExportId(exportId: string) {
    return this.http.post<ResponseModel>(ApiConstants.QUOTE_ORDER_VALIDATE_SRD_BY_EXPORT_ID_URL + exportId, {})
      .pipe(map((response) => response.data), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }
}
